/** OutstandingBars — per-project stacked paid/outstanding bars. */

import type { Project, InterimApplication } from '../../types/database';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { OutstandingChart } from '../../shared-ui/home/OutstandingChart';
import { buildOutstandingRows } from '../../shared-ui/home/homeModels';
import { fmtMoney } from './utils';

interface OutstandingBarsProps {
  projects: Project[];
  applications: InterimApplication[];
  maxRows?: number;
  onOpen?: (projectId: string) => void;
}

export function OutstandingBars({
  projects,
  applications,
  maxRows = 6,
  onOpen,
}: OutstandingBarsProps) {
  const isPhone = useMediaQuery('(max-width: 767px)');
  // Rows are shared with the native app; phones draw the shared chart.
  const rows = buildOutstandingRows(projects, applications, maxRows).map((r) => ({ ...r, p: r.project }));
  if (isPhone) return <OutstandingChart projects={projects} applications={applications} maxRows={maxRows} onOpen={onOpen} />;

  if (rows.length === 0) {
    return (
      <div className="px-4 pt-5 pb-3 font-apple">
        <div className="relative h-[84px] @[700px]/dash:h-[130px] ml-6 border-b border-border/70" role="img" aria-label="Certified work chart: no certified work yet">
          <div className="absolute inset-x-0 top-0 border-t border-border/40" />
          <div className="absolute inset-x-0 top-1/2 border-t border-border/40" />
          <span className="absolute right-full bottom-0 pr-2 text-[10px] text-muted-foreground">£0</span>
          <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-xs text-muted-foreground bg-card py-1">No certified work yet</p>
        </div>
        <div className="flex justify-center gap-5 mt-3 text-[11px] text-muted-foreground">
          <span>Paid</span><span>Outstanding</span>
        </div>
      </div>
    );
  }

  const maxCert = Math.max(...rows.map((r) => r.certified), 1);

  return (
    <div className="px-4 py-3 font-apple flex flex-col gap-3.5">
      {rows.map(({ p, certified, paid, outstanding, nextDays }) => {
        const paidPct = certified > 0 ? (paid / certified) * 100 : 0;
        const outPct = certified > 0 ? (outstanding / certified) * 100 : 0;
        const barWidthPct = (certified / maxCert) * 100;
        const isOverdue = nextDays != null && nextDays < 0;
        const isDueSoon = nextDays != null && nextDays >= 0 && nextDays <= 7;

        return (
          // Affordance only when there is somewhere to go: Home renders this
          // with no onOpen, and a pointer cursor plus a hover colour change on
          // a row that does nothing is a promise the card can't keep.
          <div
            key={p.id}
            className={onOpen ? 'group cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring' : undefined}
            role={onOpen ? 'button' : undefined}
            tabIndex={onOpen ? 0 : undefined}
            onKeyDown={onOpen ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen(p.id);
              }
            } : undefined}
            onClick={onOpen ? () => onOpen(p.id) : undefined}
          >
            <div className="flex items-start justify-between mb-2 gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm leading-5 font-medium text-foreground break-words group-hover:text-brand transition-colors">
                  {p.name}
                </p>
                {nextDays != null && (
                  <span
                    className="mt-0.5 block text-xs leading-5 tabular-nums"
                    style={{
                      color: isOverdue
                        ? 'var(--status-danger)'
                        : isDueSoon
                        ? 'var(--status-warning)'
                        : 'var(--muted-foreground)',
                    }}
                  >
                    {isOverdue
                      ? `${Math.abs(nextDays)}d overdue`
                      : nextDays === 0
                      ? 'due today'
                      : `${nextDays}d`}
                  </span>
                )}
              </div>
              <div className="shrink-0 pt-0.5">
                {outstanding > 0 ? (
                  <span className="text-sm font-semibold tabular-nums text-status-warning">
                    {fmtMoney(outstanding)}
                  </span>
                ) : (
                  <span className="text-sm text-status-success tabular-nums">clear</span>
                )}
              </div>
            </div>

            <div className="h-3 bg-muted/30 rounded-full overflow-hidden">
              <div
                className="h-full flex rounded-full overflow-hidden"
                style={{ width: `${barWidthPct}%` }}
              >
                <div
                  className="h-full shrink-0"
                  style={{ width: `${paidPct}%`, background: 'var(--status-success)', opacity: 0.65 }}
                />
                {outstanding > 0 && (
                  <div
                    className="h-full shrink-0"
                    style={{ width: `${outPct}%`, background: 'var(--status-warning)', opacity: 0.8 }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Legend */}
      <div className="flex items-center gap-4 pt-1 border-t border-border/30">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="w-2.5 h-2.5 rounded-sm inline-block shrink-0"
            style={{ background: 'var(--status-success)', opacity: 0.65 }}
          />
          Paid
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="w-2.5 h-2.5 rounded-sm inline-block shrink-0"
            style={{ background: 'var(--status-warning)', opacity: 0.8 }}
          />
          Outstanding
        </span>
      </div>
    </div>
  );
}
